/**
 * Le zoo: un batiment, ses enclos, et les animaux qui y vivent.
 *
 * DEUX REGLES DU PROPRIETAIRE COMMANDENT TOUT CE FICHIER.
 *
 * 1. On ne chasse pas, on ne capture pas. Un animal ne rejoint donc jamais un
 *    enclos par la force: on l'APPRIVOISE en le nourrissant (voir `nourrir` dans
 *    shared/sim/animaux.ts), il finit par suivre celui qui l'a nourri, et c'est
 *    a pied qu'il entre chez lui. Le zoo se merite.
 * 2. On ne perd jamais rien. Rendre un animal a la nature ne defait pas
 *    l'amitie: il reste familier, il revient si on l'appelle.
 *
 * CE QU'UN ENCLOS EST DEVENU. C'etait une aire que le joueur fermait lui-meme
 * avec ses clotures, reconnue par un remplissage par diffusion sur six mille
 * quatre cents cases. Sur le papier, on dessinait le zoo qu'on voulait. En jeu,
 * `MARGE_BATIMENT` interdit deux clotures bout a bout: une barriere reelle est
 * donc toujours trouee d'environ un metre, il a fallu inventer une marge pour
 * refermer ces trous-la, et il restait un jeu d'adresse ou l'on s'entendait
 * repondre « il reste un passage » sans jamais savoir lequel.
 *
 * Le zoo est maintenant UN BATIMENT qu'on pose, avec son pavillon et ses trois
 * enclos deja dessines (`PLAN_ZOO`, shared/data/zoo.ts). Reconnaitre un enclos
 * n'est plus un algorithme: c'est une boite, une rotation, et deux comparaisons.
 * Six mille quatre cents cases de tampons ont disparu avec.
 *
 * Pur, comme tout shared/sim: ni DOM, ni three.js, ni horloge.
 */
import type { Animal, Batiment } from '../types/monde.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { BATIMENTS } from '../data/batiments.ts'
import { ANIMAUX, PROFILS } from '../data/animaux.ts'
import { Pronom, accorde, leComplement } from './grammaire.ts'
import { CAPACITE_ENCLOS, PLAN_ZOO, type PieceInterieure } from '../data/zoo.ts'
import {
  aLeMeuble,
  boiteEtage,
  cloisonsInterieures,
  dansEmprise,
  positionAnimal,
  tourner,
  tournerInverse,
} from './batiments.ts'
import { dist } from './alea.ts'
import { REPAS_APPRIVOISEMENT, estFamilier, installerFoyer, repasRestants } from './animaux.ts'

/** Reponse a « est-ce que je peux ? », avec une phrase pour le joueur. */
export interface Verdict {
  ok: boolean
  /** Message francais affichable tel quel. Toujours present quand ok est faux. */
  raison?: string
}

// ------------------------------------------------------------------ enclos

/**
 * Un enclos reconnu: quel zoo, lequel de ses enclos, et ou vivent ses hotes.
 *
 * `centre` et `rayon` sont dans le repere du MONDE et sont le foyer des
 * pensionnaires — exactement ce que `installerFoyer` attend. Ils se deduisent
 * du batiment et du plan, jamais de la position de l'animal: deux betes
 * installees depuis deux coins opposes du meme enclos obtiennent donc le meme
 * foyer, au bit pres, ce qui est la condition pour que `pensionnaires` les
 * reconnaisse comme voisines.
 */
export interface Enclos {
  zoo: Batiment
  /** Index dans `cloisonsInterieures(zoo.kind)`: identifie l'enclos de facon stable. */
  index: number
  /** Nom affichable, accentue. */
  nom: string
  /** Vrai si l'enclos a un BASSIN: les especes d'eau peuvent y vivre. */
  bassin: boolean
  centre: Vec2
  /** Rayon de deambulation sur: la bete ne traverse jamais la cloison. */
  rayon: number
  capacite: number
}

/**
 * Nombre d'enclos du zoo. Deduit du PLAN, jamais ecrit deux fois: le mode
 * d'emploi affiche ce chiffre au joueur, et il devait rester vrai le jour ou
 * l'on ajoute un quatrieme enclos.
 */
export const NOMBRE_ENCLOS = PLAN_ZOO.length

/** Nom de l'enclos qui porte le bassin, pour pouvoir y renvoyer le joueur. */
export const NOM_BASSIN = PLAN_ZOO.find((p) => p.bassin)?.nom ?? 'le bassin'

/**
 * Marge entre le foyer d'un enclos et sa cloison, en metres.
 *
 * Elle fixe le rayon ou la bete deambule: assez de retrait pour qu'elle ne
 * traverse jamais la barriere, assez peu pour qu'elle ne soit pas figee au
 * milieu. Un enclos fait 3,6 x 4,4 m, donc 60 cm laissent 1,2 m de rayon.
 */
const MARGE_FOYER = 0.6

/** Le foyer d'une piece d'un batiment, dans le repere du monde. */
function foyerDe(b: Batiment, piece: PieceInterieure, index: number): Enclos {
  const boite = boiteEtage(b.kind, piece)
  const v = tourner(boite.cx, boite.cz, b.rot)
  return {
    zoo: b,
    index,
    nom: piece.nom,
    bassin: piece.bassin === true,
    centre: { x: b.pos.x + v.x, z: b.pos.z + v.z },
    // La plus petite demi-dimension, moins la marge: un animal qui tourne dans
    // ce disque-la ne sort jamais de sa piece, quelle que soit la rotation.
    rayon: Math.max(0.8, Math.min(boite.hx, boite.hz) - MARGE_FOYER),
    capacite: CAPACITE_ENCLOS,
  }
}

/** Tous les enclos de ce zoo, dans l'ordre du plan. */
export function enclosDe(b: Batiment): Enclos[] {
  const sortie: Enclos[] = []
  const pieces = cloisonsInterieures(b.kind)
  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i]
    // Seules les pieces marquees `enclos` comptent: les chambres de l'hotel
    // passent par la meme table de cloisons, et une chambre n'heberge pas de
    // pensionnaire. L'index reste celui de la table entiere, donc stable.
    if (!piece || piece.enclos !== true) continue
    sortie.push(foyerDe(b, piece, i))
  }
  return sortie
}

/**
 * L'enclos qui contient ce point, sinon null.
 *
 * C'EST TOUTE LA RECONNAISSANCE, et elle tient en une boucle sur trois boites.
 * Elle remplace `analyserEnclos`, ses quatre tampons de 6 400 cases et son
 * remplissage par diffusion — et surtout elle rend TOUJOURS la meme reponse au
 * meme endroit, ce que la diffusion ne garantissait pas: il suffisait qu'un
 * joueur pose une cloture a vingt metres de la pour que l'aire close change de
 * forme, donc de centre, donc de foyer.
 */
export function enclosAuPoint(batiments: readonly Batiment[], x: number, z: number): Enclos | null {
  for (const b of batiments) {
    const pieces = cloisonsInterieures(b.kind)
    if (pieces.length === 0) continue
    // Elimination par l'emprise avant de regarder les pieces: un monde peut
    // compter des dizaines de batiments et un seul zoo.
    if (!dansEmprise(b, x, z)) continue
    const local = tournerInverse(x - b.pos.x, z - b.pos.z, b.rot)
    for (let i = 0; i < pieces.length; i++) {
      const piece = pieces[i]
      // Une chambre d'hotel n'est pas un enclos: voir `enclosDe`.
      if (!piece || piece.enclos !== true) continue
      const boite = boiteEtage(b.kind, piece)
      if (Math.abs(local.x - boite.cx) <= boite.hx && Math.abs(local.z - boite.cz) <= boite.hz) {
        return foyerDe(b, piece, i)
      }
    }
  }
  return null
}

/** Ce qui manque encore, dit en francais. */
export function verdictEnclos(e: Enclos | null): Verdict {
  if (!e) {
    return { ok: false, raison: 'Il n’y a pas d’enclos ici : fais-le entrer dans un enclos du zoo.' }
  }
  return { ok: true }
}

/** Les animaux qui vivent deja dans cet enclos (meme foyer, a un metre pres). */
export function pensionnaires(animaux: readonly Animal[], centre: Vec2): Animal[] {
  const sortie: Animal[] = []
  for (const a of animaux) {
    if (a.ancre === null || a.maison !== null) continue
    if (dist(a.ancre.x, a.ancre.z, centre.x, centre.z) <= 1) sortie.push(a)
  }
  return sortie
}

// ------------------------------------------------------------- installation

/** Vrai si un joueur a le droit de disposer de cet animal. */
function estSonAmi(a: Animal, token: string): boolean {
  return a.ami === null || a.ami === token
}

/** Phrase commune aux deux installations: il faut d'abord l'apprivoiser. */
function verdictFamilier(a: Animal, token: string): Verdict | null {
  if (!estFamilier(a)) {
    const def = ANIMAUX[a.kind]
    const reste = repasRestants(a)
    return {
      ok: false,
      raison: `${Pronom(def)} n’est pas encore ${accorde('apprivoisé', def)} : nourris-${leComplement(def)} ${reste} fois de plus (${a.repas}/${REPAS_APPRIVOISEMENT}).`,
    }
  }
  if (!estSonAmi(a, token)) return { ok: false, raison: 'Cet animal a déjà son ami : il ne te suivra pas.' }
  return null
}

export function peutInstallerDansEnclos(
  a: Animal,
  e: Enclos | null,
  animaux: readonly Animal[],
  token: string,
  terrain: ITerrain,
): Verdict {
  const familier = verdictFamilier(a, token)
  if (familier) return familier
  const v = verdictEnclos(e)
  if (!v.ok || !e) return v
  /*
   * L'ESPECE DECIDE DE SON MILIEU, pas nous. Un canard a besoin de son bassin,
   * une chevre de sa terre ferme.
   *
   * C'est le BASSIN qui repond, pas le relief — comme le spa de la terrasse,
   * qui est de l'eau sans que le terrain le soit. Un enclos pose sur une mare
   * compte aussi: le zoo se batit rarement dans l'eau, mais si on y arrive, la
   * bete a bien ce qu'il lui faut.
   */
  const milieu = PROFILS[a.kind].milieu
  if ((milieu === 'eau' || milieu === 'surface') && !e.bassin && !terrain.estEau(e.centre.x, e.centre.z)) {
    return { ok: false, raison: `Cette espèce a besoin d’eau : installe-la dans « ${NOM_BASSIN} ».` }
  }
  const deja = pensionnaires(animaux, e.centre)
  if (deja.some((x) => x.id === a.id)) return { ok: false, raison: 'Il habite déjà cet enclos.' }
  if (deja.length >= e.capacite) {
    return { ok: false, raison: `${e.nom} est plein (${deja.length}/${e.capacite}). Essaie un autre enclos.` }
  }
  return { ok: true }
}

export function installerDansEnclos(a: Animal, e: Enclos, token: string): void {
  installerFoyer(a, e.centre, e.rayon, null)
  if (a.ami === null) a.ami = token
}

// ----------------------------------------------------------------- adoption

/**
 * Rayon de deambulation d'un animal de compagnie, en metres.
 *
 * Il vit DANS la piece et AUTOUR de la maison, comme demande: assez pour faire
 * le tour du salon et sortir sur le pas de la porte, pas assez pour partir au
 * bout du jardin.
 */
export function rayonMaison(b: Batiment): number {
  const t = BATIMENTS[b.kind].taille
  return Math.max(2, Math.min(5, Math.min(t.l, t.p) / 2 + 1))
}

/** L'animal de compagnie de cette maison, s'il y en a un. */
export function animalDeLaMaison(animaux: readonly Animal[], idMaison: string): Animal | null {
  for (const a of animaux) if (a.maison === idMaison) return a
  return null
}

export function peutAdopter(
  a: Animal,
  b: Batiment,
  animaux: readonly Animal[],
  token: string,
): Verdict {
  /*
   * UNE MAISON, PAS N'IMPORTE QUEL BATIMENT OU L'ON ENTRE.
   *
   * Le test etait `habitable`, qui veut dire « on y entre » — c'est vrai de
   * l'ecole, du musee, de la bibliotheque, du garage et du ZOO lui-meme. Une
   * campagne de bout en bout a pris le defaut sur le fait: conduit dans un
   * enclos, l'animal etait adopte comme animal de compagnie DU ZOO, et
   * `accueilPour` ne descendait donc jamais jusqu'a l'enclos.
   *
   * La reponse vient de la DONNEE, et de la seule qui dise vraiment « on habite
   * ici »: le coin de l'animal, ce panier dessine au fond de la piece
   * (`meublesDe`). Ce qu'on voit est exactement ce avec quoi on interagit,
   * comme partout ailleurs — et un modele ajoute demain sera range du bon cote
   * par son ameublement, sans qu'on ait a retrouver cette ligne.
   */
  if (!aLeMeuble(b.kind, 'animal')) {
    return { ok: false, raison: 'On n’adopte un animal que dans une maison, pas ici.' }
  }
  if (b.proprietaire !== token) return { ok: false, raison: 'Cette maison n’est pas la tienne.' }
  const familier = verdictFamilier(a, token)
  if (familier) return familier
  const milieu = PROFILS[a.kind].milieu
  if (milieu === 'eau' || milieu === 'surface') {
    return { ok: false, raison: 'Cette espèce vit dans l’eau : elle ne peut pas habiter une maison.' }
  }
  if (a.maison === b.id) return { ok: false, raison: 'Il habite déjà ici.' }
  const occupant = animalDeLaMaison(animaux, b.id)
  if (occupant && occupant.id !== a.id) {
    return { ok: false, raison: 'Cette maison a déjà son animal de compagnie.' }
  }
  if (!dansEmprise(b, a.pos.x, a.pos.z, 2)) return { ok: false, raison: 'Fais-le d’abord entrer chez toi.' }
  return { ok: true }
}

/** Adopte l'animal: son panier est le coin de l'animal de la maison. */
export function adopter(a: Animal, b: Batiment, token: string): void {
  installerFoyer(a, positionAnimal(b), rayonMaison(b), b.id)
  if (a.ami === null) a.ami = token
}
